import { CANVAS_SIZE } from '@/constant';
import { capture } from '@/handlers/error';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { ResizeBorder } from '@/pages/LessonEditor/components/ResizeBorder';
import { useComponentEditor, useSlideEditor } from '@/providers/editor/EditorProvider';
import { Coordinate, isSlideComponentBackground, PropsWithPreview, Size, SlideComponent } from '@/types/slide';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FlipIcon from '@mui/icons-material/Flip';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import { Box } from '@mui/material';
import { useNotifications } from '@toolpad/core';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'rooks';
import { ImagePicker, ImagePickerLibrarySourceType } from '../inputs/ImagePicker/ImagePicker';
import { ComponentLabel } from './ComponentLabel';
import { ContextualBar } from './ContextualBar';
import { useRightClickMenu } from './hooks/useRightClickMenu';
import { useSlideComponent } from './hooks/useSlideComponent';
import { RightClickMenu } from './RightClickMenu';
import { SlideRef } from './Slide';

interface Props {
    ref: React.RefObject<HTMLDivElement | null>;
    init: {
        model: SlideComponent;
        focus?: boolean;
    };
    slideRef: React.RefObject<SlideRef | null>;
}

export const SlideComponentBackground: React.FC<PropsWithPreview<Props>> = ({ ref, init, preview, slideRef }) => {
    const { t } = useTranslation();
    const notifications = useNotifications();
    const { selectedSlide } = useSlideEditor();
    const { deleteComponent, updateComponent, createSlideImage, copyComponent, pasteComponent, undo, redo } = useComponentEditor();
    const { rightClickMenu, handleRightClickMenu, rightClickMenuClose } = useRightClickMenu();

    // Add refs for the popovers
    const imagePickerRef = useRef<HTMLDivElement | null>(null);

    const { isSelected } = useSlideComponent(ref, [], !!preview);

    const [model, setModel] = useState<SlideComponent>({ ...init.model });
    const [isFocus, setIsFocus] = useState<boolean>(init.focus ?? false);

    const [tSize, setTSize] = useState<Size>(model.size);
    const [tPos, setTPos] = useState<Coordinate>(model.pos);

    // Add state for image picker popover
    const [imagePickerAnchorEl, setImagePickerAnchorEl] = useState<HTMLElement | null>(null);

    const isEditing = isFocus || isSelected;

    const onDeleteComponent = React.useCallback(
        async (id: string) => {
            try {
                await deleteComponent(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [deleteComponent, notifications],
    );

    const onUpdateComponent = React.useCallback(
        async (id: string, component: SlideComponent) => {
            try {
                await updateComponent(id, component);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [updateComponent, notifications],
    );

    const onUploadImage = React.useCallback(
        async (file: File): Promise<string> => {
            try {
                return await createSlideImage(selectedSlide.editorData?.id ?? '', file);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
                return '';
            }
        },
        [createSlideImage, notifications, selectedSlide.editorData?.id],
    );

    const debouncedUpdateComponent = useDebounce(onUpdateComponent, 500);

    useOutsideClick(ref, () => {
        if (preview) return;
        if (!ref) return;

        setIsFocus(false);
    });

    useEffect(() => {
        if (isFocus && !preview) {
            setImagePickerAnchorEl(ref.current);
        }
    }, [isFocus, preview, ref]);

    if (!isSlideComponentBackground(model)) {
        return null;
    }

    const backgroundLibrarySource: ImagePickerLibrarySourceType[] = [
        { path: '/asset/images/scene1_2.png', value: 'scene1_2.png', label: '公園', size: { width: 2048, height: 1536 } },
        { path: '/asset/images/scene1_3.png', value: 'scene1_3.png', label: '公園', size: { width: 2048, height: 1536 } },
        { path: '/asset/images/scene2_2.png', value: 'scene2_2.png', label: '沙灘', size: { width: 2048, height: 1536 } },
        { path: '/asset/images/scene2_3.png', value: 'scene2_3.png', label: '沙灘', size: { width: 2048, height: 1536 } },
        { path: '/asset/images/scene3_2.png', value: 'scene3_2.png', label: '旅行', size: { width: 2048, height: 1536 } },
        { path: '/asset/images/scene3_3.png', value: 'scene3_3.png', label: '旅行', size: { width: 2048, height: 1536 } },
        { path: '/asset/images/scene4_2.png', value: 'scene4_2.png', label: '學校', size: { width: 2048, height: 1536 } },
        { path: '/asset/images/scene4_3.png', value: 'scene4_3.png', label: '學校', size: { width: 2048, height: 1536 } },
        { path: '/asset/images/scene5_2.png', value: 'scene5_2.png', label: '家庭', size: { width: 2048, height: 1536 } },
        { path: '/asset/images/scene5_3.png', value: 'scene5_3.png', label: '家庭', size: { width: 2048, height: 1536 } },
        { path: '/asset/images/scene6_2.png', value: 'scene6_2.png', label: '飲茶', size: { width: 2048, height: 1536 } },
        { path: '/asset/images/scene6_3.png', value: 'scene6_3.png', label: '飲茶', size: { width: 2048, height: 1536 } },
        { path: '/asset/images/tt_lesson-background_01.png', value: 'tt_lesson-background_01.png', label: '旅龜學習', size: { width: 2048, height: 1536 } },
        { path: '/asset/images/tt_lesson-background_02.png', value: 'tt_lesson-background_02.png', label: '旅龜學習', size: { width: 2048, height: 1536 } },
        { path: '/asset/images/tt_lesson-background_03.png', value: 'tt_lesson-background_03.png', label: '旅龜學習', size: { width: 2048, height: 1536 } },
        { path: '/asset/images/tt_lesson-background_04.png', value: 'tt_lesson-background_04.png', label: '旅龜學習', size: { width: 2048, height: 1536 } },
    ];

    const isPlaceholder = !model.content.imageURL && !model.content.filename;

    return (
        <Box
            onContextMenu={isEditing ? handleRightClickMenu : undefined}
            ref={ref}
            id={`background-component-${model.id}`}
            sx={{
                zIndex: -1,
                position: 'absolute',
                left: tPos.x,
                top: tPos.y,
                m: 'inherit',
                p: 'inherit',
                textAlign: 'left',
                width: tSize.width,
                height: tSize.height,
            }}
            onClick={(e) => {
                if (!preview) {
                    e.stopPropagation();
                }
            }}
            onDoubleClick={() => {
                if (preview) return;
                setImagePickerAnchorEl(ref.current);
            }}
        >
            <ResizeBorder
                resizable={false}
                disabled
                size={tSize}
                pos={tPos}
                preview={preview}
                isSelected={isEditing}
                onResize={(size, pos) => {
                    setTSize(size);
                    setTPos(pos);
                    const updateModel = { ...model, size, pos };
                    setModel({ ...updateModel });
                    debouncedUpdateComponent?.(model.id, { ...updateModel });
                }}
                slideRef={slideRef}
            >
                {!preview && (
                    <ComponentLabel
                        variant='caption'
                        sx={{
                            ...(!isEditing && {
                                opacity: 0, // Hide by default
                                transition: 'opacity 0.2s ease',
                                [`#background-component-${model.id}:hover &`]: {
                                    opacity: 1, // Show when text-component is hovered
                                },
                            }),
                        }}
                        noWrap
                    >
                        {t('slide.asset.background')}
                    </ComponentLabel>
                )}

                <Box
                    id='background-component'
                    sx={{
                        m: 'inherit',
                        p: 'inherit',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transform: `scaleX(${model.content.flip ?? 1})`,
                    }}
                >
                    {isPlaceholder ? (
                        <Box
                            sx={{
                                backgroundColor: (theme) => (theme.vars || theme).palette.grey[200],
                                width: '100%',
                                height: '100%',
                                aspectRatio: 'auto',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <WallpaperIcon fontSize='large' />
                        </Box>
                    ) : (
                        <Box
                            component='img'
                            alt='bg'
                            src={model.content.imageURL ? model.content.imageURL : `/asset/images/${model.content.filename || 'default.png'}`}
                            sx={{
                                WebkitTouchCallout: 'none',
                                WebkitUserSelect: 'none',
                                KhtmlUserSelect: 'none',
                                MozUserSelect: 'none',
                                msUserSelect: 'none',
                                userSelect: 'none',
                                pointerEvents: 'none',
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                            }}
                        />
                    )}
                </Box>

                {!preview && isEditing && (
                    <ContextualBar
                        startButtons={[
                            {
                                icon: <EditIcon />,
                                tooltip: t('slide.components.assets.text.tooltip.edit'),
                                onClick: () => {
                                    setImagePickerAnchorEl(ref.current);
                                },
                            },
                            {
                                icon: <FlipIcon />,
                                tooltip: t('slide.components.assets.text.tooltip.flip'),
                                onClick: () => {
                                    const updatedContent = { ...model.content, flip: model.content.flip === 1 ? -1 : 1 };
                                    const updateModel = { ...model, content: updatedContent };
                                    setModel(updateModel);
                                    onUpdateComponent?.(model.id, updateModel);
                                },
                            },
                        ]}
                        endButtons={[
                            {
                                icon: <DeleteIcon />,
                                tooltip: t('slide.components.assets.text.tooltip.remove'),
                                onClick: () => {
                                    onDeleteComponent?.(model.id);
                                },
                            },
                        ]}
                    />
                )}

                <ImagePicker
                    type='background'
                    disableSearch
                    title={t('slide.asset.background')}
                    librarySources={backgroundLibrarySource}
                    anchorEl={imagePickerAnchorEl}
                    slotProps={{
                        paper: {
                            ref: imagePickerRef, // Attach the ref to the Paper element
                        },
                    }}
                    value={{
                        value: model.content?.filename,
                        imageURL: model.content?.imageURL,
                    }}
                    onClose={() => {
                        setImagePickerAnchorEl(null);
                        setIsFocus(false);
                    }}
                    onUpdate={(settings) => {
                        const updatedContent = { ...model.content, filename: settings.value, imageURL: settings.imageURL };
                        const updateModel = { ...model, content: updatedContent, size: CANVAS_SIZE, pos: { x: 0, y: 0 } };

                        setTPos(updateModel.pos);
                        setTSize(updateModel.size);

                        setModel(updateModel);
                        onUpdateComponent?.(model.id, updateModel);
                    }}
                    onUpload={onUploadImage}
                />
            </ResizeBorder>

            <RightClickMenu
                componentId={model.id}
                contextMenu={rightClickMenu}
                handleClose={rightClickMenuClose}
                copyComponent={copyComponent}
                pasteComponent={pasteComponent}
                undo={undo}
                redo={redo}
            />
        </Box>
    );
};
